import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { TrendingUp, Filter, Search, Star, Target, Zap } from 'lucide-react'
import { usePredictions } from '../hooks/usePredictions'

interface PredictionsSectionProps {
  setIsLoading: (loading: boolean) => void
}

const PredictionsSection = ({ setIsLoading }: PredictionsSectionProps) => {
  const [selectedPosition, setSelectedPosition] = useState<string>('all')
  const [searchTerm, setSearchTerm] = useState('')
  const [sortBy, setSortBy] = useState<'points' | 'confidence' | 'form'>('points')
  
  const { predictions, loading, error, fetchPredictions } = usePredictions()

  useEffect(() => {
    fetchPredictions()
  }, [fetchPredictions])

  useEffect(() => {
    setIsLoading(loading)
  }, [loading, setIsLoading])

  const positions = [
    { value: 'all', label: 'All Positions', color: 'from-fpl-green to-fpl-blue' },
    { value: '1', label: 'Goalkeepers', color: 'from-fpl-yellow to-fpl-orange' },
    { value: '2', label: 'Defenders', color: 'from-fpl-blue to-fpl-cyan' },
    { value: '3', label: 'Midfielders', color: 'from-fpl-green to-fpl-emerald' },
    { value: '4', label: 'Forwards', color: 'from-fpl-red to-fpl-pink' },
  ]

  const filteredPredictions = predictions
    .filter(pred => selectedPosition === 'all' || pred.position === positions.find(p => p.value === selectedPosition)?.label)
    .filter(pred => pred.player_name.toLowerCase().includes(searchTerm.toLowerCase()))
    .sort((a, b) => {
      switch (sortBy) {
        case 'points': return b.predicted_points - a.predicted_points
        case 'confidence': return b.confidence - a.confidence
        case 'form': return parseFloat(b.factors.form_factor) - parseFloat(a.factors.form_factor)
        default: return 0
      }
    })

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  }

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 }
  }

  return (
    <section id="predictions" className="py-20 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl sm:text-4xl font-bold mb-4">
            <span className="gradient-text">Player Predictions</span>
          </h2>
          <p className="text-xl text-fpl-white/80 max-w-2xl mx-auto">
            AI-powered predictions for upcoming gameweeks based on form, fixtures, and advanced statistics
          </p>
        </motion.div>

        {/* Filters and Search */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="mb-8"
        >
          <div className="flex flex-col lg:flex-row gap-4 items-center justify-between">
            {/* Position Filter */}
            <div className="flex flex-wrap gap-2">
              {positions.map((position) => (
                <motion.button
                  key={position.value}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setSelectedPosition(position.value)}
                  className={`px-4 py-2 rounded-lg font-medium transition-all duration-200 ${
                    selectedPosition === position.value
                      ? `bg-gradient-to-r ${position.color} text-white shadow-lg`
                      : 'bg-fpl-light-gray text-fpl-white/70 hover:bg-fpl-light-gray/80'
                  }`}
                >
                  {position.label}
                </motion.button>
              ))}
            </div>

            {/* Search and Sort */}
            <div className="flex flex-col sm:flex-row gap-4 items-center">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-fpl-white/50" />
                <input
                  type="text"
                  placeholder="Search players..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 pr-4 py-2 bg-fpl-light-gray border border-white/20 rounded-lg text-white placeholder-white/50 focus:outline-none focus:border-fpl-green"
                />
              </div>
              
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value as any)}
                className="px-4 py-2 bg-fpl-light-gray border border-white/20 rounded-lg text-white focus:outline-none focus:border-fpl-green"
              >
                <option value="points">Sort by Points</option>
                <option value="confidence">Sort by Confidence</option>
                <option value="form">Sort by Form</option>
              </select>
            </div>
          </div>
        </motion.div>

        {/* Predictions Grid */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
        >
          <AnimatePresence>
            {filteredPredictions.slice(0, 12).map((prediction, index) => (
              <motion.div
                key={prediction.player_id}
                variants={itemVariants}
                whileHover={{ y: -5, scale: 1.02 }}
                className="glass-effect p-6 rounded-xl card-hover"
              >
                {/* Player Header */}
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="text-lg font-semibold text-fpl-white">{prediction.player_name}</h3>
                    <p className="text-sm text-fpl-white/70">{prediction.team} • {prediction.position}</p>
                  </div>
                  <div className="text-right">
                    <div className="text-2xl font-bold text-fpl-green">{prediction.predicted_points.toFixed(1)}</div>
                    <div className="text-xs text-fpl-white/60">predicted pts</div>
                  </div>
                </div>

                {/* Confidence Bar */}
                <div className="mb-4">
                  <div className="flex items-center justify-between text-sm mb-2">
                    <span className="text-fpl-white/70">Confidence</span>
                    <span className="text-fpl-green font-medium">{(prediction.confidence * 100).toFixed(0)}%</span>
                  </div>
                  <div className="w-full bg-fpl-light-gray rounded-full h-2">
                    <motion.div
                      initial={{ width: 0 }}
                      whileInView={{ width: `${prediction.confidence * 100}%` }}
                      viewport={{ once: true }}
                      transition={{ duration: 1, delay: index * 0.1 }}
                      className="bg-gradient-to-r from-fpl-green to-fpl-blue h-2 rounded-full"
                    />
                  </div>
                </div>

                {/* Factors */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-fpl-white/70">xG Contribution</span>
                    <span className="text-fpl-white">{prediction.factors.xg_contribution}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-fpl-white/70">Clean Sheet Prob</span>
                    <span className="text-fpl-white">{(prediction.factors.clean_sheet_probability * 100).toFixed(0)}%</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-fpl-white/70">Fixture Difficulty</span>
                    <span className="text-fpl-white">{prediction.factors.fixture_difficulty}/5</span>
                  </div>
                </div>

                {/* Next Fixtures */}
                {prediction.next_fixtures && prediction.next_fixtures.length > 0 && (
                  <div className="mt-4 pt-4 border-t border-white/20">
                    <h4 className="text-sm font-medium text-fpl-white/80 mb-2">Next Fixtures</h4>
                    <div className="space-y-1">
                      {prediction.next_fixtures.slice(0, 2).map((fixture, idx) => (
                        <div key={idx} className="flex items-center justify-between text-xs">
                          <span className="text-fpl-white/70">
                            {fixture.opponent} ({fixture.home_away})
                          </span>
                          <span className="text-fpl-green">{fixture.predicted_points.toFixed(1)} pts</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </motion.div>
            ))}
          </AnimatePresence>
        </motion.div>

        {/* Load More Button */}
        {filteredPredictions.length > 12 && (
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-center mt-8"
          >
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="button-secondary"
            >
              Load More Predictions
            </motion.button>
          </motion.div>
        )}

        {/* Error State */}
        {error && (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="text-center py-8"
          >
            <div className="text-fpl-red text-lg mb-2">Failed to load predictions</div>
            <p className="text-fpl-white/70 mb-4">{error}</p>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={fetchPredictions}
              className="button-primary"
            >
              Try Again
            </motion.button>
          </motion.div>
        )}
      </div>
    </section>
  )
}

export default PredictionsSection
